import type { CSSProperties } from 'react';

export interface StatusHistoryEntry {
    status: string;
    changedAt: string;
    changedById?: string | null;
    changedBySource?: string | null;
    trigger: string;
}

export interface OpeningLogEntry {
    openedById?: string | null;
    openedBySource?: string | null;
    openedAt: string;
    success: boolean;
    failureReason?: string | null;
}

export type HandoverStatusType =
    | 'RESERVED'
    | 'DEPOSITED'
    | 'PICKED_UP'
    | 'COMPLETED'
    | 'CANCELLED'
    | 'CANCELLED_ADMIN'
    | 'EXPIRED'
    | 'EXPIRED_DEPOSIT'
    | 'AUTO_PICKED_UP'
    | 'ESCALATED_ADMIN';

export interface HandoverRecord {
    id: string;
    status: HandoverStatusType;
    depositorId: string;
    recipientId: string;
    stationId: string;
    stationShortId?: string | null;
    slotId: string;
    slotShortId?: string | null;
    slotSize: string;
    referenceId?: string | null;
    createdDate: string;
    expiresAt?: string | null;
    reservedAt?: string;
    depositedAt?: string;
    pickedUpAt?: string;
    completedAt?: string;
    cancelledAt?: string;
    statusHistory?: StatusHistoryEntry[];
    openingLog?: OpeningLogEntry[];
}

export const handoverStatusChoices = [
    { id: 'RESERVED', name: 'Reserviert' },
    { id: 'DEPOSITED', name: 'Eingelagert' },
    { id: 'PICKED_UP', name: 'Abgeholt' },
    { id: 'COMPLETED', name: 'Abgeschlossen' },
    { id: 'CANCELLED', name: 'Abgebrochen' },
    { id: 'CANCELLED_ADMIN', name: '⛔ Admin storniert' },
    { id: 'EXPIRED', name: 'Abgelaufen' },
    { id: 'EXPIRED_DEPOSIT', name: 'Einlagerung abgelaufen' },
    { id: 'AUTO_PICKED_UP', name: 'Automatisch abgeholt' },
    { id: 'ESCALATED_ADMIN', name: '⚠ Admin-Eskalation' },
];

export const TIMEOUT_WARNING_HOURS = 2;
const ACTIVE_STATUSES: HandoverStatusType[] = ['RESERVED', 'DEPOSITED'];

export const TERMINAL_STATUSES: HandoverStatusType[] = [
    'PICKED_UP', 'COMPLETED', 'CANCELLED', 'CANCELLED_ADMIN', 'EXPIRED', 'EXPIRED_DEPOSIT', 'AUTO_PICKED_UP',
];

export const isTimeoutWarning = (expiresAt: string, status: HandoverStatusType): boolean => {
    if (!ACTIVE_STATUSES.includes(status)) return false;
    const diffHours = (new Date(expiresAt).getTime() - Date.now()) / (1000 * 60 * 60);
    return diffHours > 0 && diffHours <= TIMEOUT_WARNING_HOURS;
};

// WCAG-konforme Farben:
// Rot: #ffebee Hintergrund — Standard-Textfarbe (schwarz) hat >7:1 Kontrast (WCAG 1.4.3 ✓, 1.4.11 ✓)
// Gelb: #fffde7 Hintergrund — Standard-Textfarbe (schwarz) hat >7:1 Kontrast (WCAG 1.4.3 ✓, 1.4.11 ✓)
export const ROW_STYLE_ESCALATED = { backgroundColor: '#ffebee' } as const;
export const ROW_STYLE_TIMEOUT_WARNING = { backgroundColor: '#fffde7' } as const;

const ESCALATED_STATUS: HandoverStatusType = 'ESCALATED_ADMIN';

export const renderStationShortId = (record: HandoverRecord): string => {
    if (record?.stationShortId) return record.stationShortId;
    if (!record?.stationId) return '–';
    return `${record.stationId.substring(0, 8)}…`;
};

export const renderSlotShortId = (record: HandoverRecord): string => {
    if (record?.slotShortId) return record.slotShortId;
    if (!record?.slotId) return '–';
    return `${record.slotId.substring(0, 8)}…`;
};

// Modul-Konstante außerhalb Komponente — kein useCallback nötig (Performance AC)
export const handoverRowStyle = (record: HandoverRecord): CSSProperties => {
    const isOpen = !TERMINAL_STATUSES.includes(record?.status);
    if (isOpen && record?.status === ESCALATED_STATUS) {
        return ROW_STYLE_ESCALATED;
    }
    if (record?.expiresAt && isTimeoutWarning(record.expiresAt, record.status)) {
        return ROW_STYLE_TIMEOUT_WARNING;
    }
    return {};
};
